type ApiBookingPart = (readonly [
    ApiSchema['Activity'],
    ApiSchema['Admission'],
    ApiSchema['Reservation'],
    ApiSchema['Accommodation'],
    ApiSchema['Ancillary'],
])[number];

/**
 * Take all booking parts from multiple "bookedOffers" an API response. Then flatten them into a single list, sorted by type.
 * - Remove EXCHANGED items.
 *
 * Why we do this?
 * The API response keeps all booking parts, even if the were exchanged or removed. Multiple additional
 * "bookedOffers" are created when exchanging. You only see a history of mutations, not the "current state".
 * It is therefore wrong to assume that "bookedOffer" is a logical, consistent unit. Instead, we need to flatten all booking parts
 * because only the single booking parts are relevant. We can then remove all exchanged ones and then have up-to-date list of booking parts.
 *
 * Unfortunately, we no longer know which parts actually belong together. E.g. we don't know if a passenger booked "Interlaken --> First --> Interlaken"
 * as a single return-trip offer or if he booked two separate one-way offers. Therefore, we need to reconstruct logical groups later on.
 *
 * Why do we only remove "EXCHANGED", but keep "REFUNDED"?
 * Because "REFUNDED" parts are still relevant. We still want to show this parts in the UI, but marked as refunded (grayed out). Where as "EXCHANGED"
 *
 */
export function flattenBookingParts(response: ApiSchema['BookingResponse']): ApiBookingPart[] {
    const sortOrder = ['Activity', 'Admission', 'Reservation', 'Accommodation', 'Ancillary'];

    return response?.booking?.bookedOffers
        ?.flatMap(bo => [
            ...(bo.activities || []),
            ...(bo.admissions || []),
            ...(bo.reservations || []),
            ...(bo.accommodations || []),
            ...(bo.ancillaries || []),
        ])
        .filter(op => op.status !== 'EXCHANGED')

        .sort((a, b) => sortOrder.indexOf(a.objectType) - sortOrder.indexOf(b.objectType))
        ?? [];
}
